import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { hrApi } from '@/api';
import { PageActionGate } from '@/components/auth/PageActionGate';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column, TableRowActions } from '@/components/ui/DataTable';
import { FormGrid } from '@/components/ui/FormGrid';
import { FormActions } from '@/components/ui/FormActions';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import type {
  HrDocumentType,
  HrEmploymentType,
  HrExpenseType,
  HrLeavePolicy,
  HrLeaveType,
  HrOvertimeType,
  HrOrgStructure,
  HrRecruitmentPipelineStage,
  HrShiftType,
  HrWorkforceSettings,
} from '@/types/hr';
import { refreshOrgStructureCache } from '@/components/selects';
import { HrOrgMasterSection, ORG_MASTER_TABS, isOrgMasterTab, type OrgMasterTab } from '@/modules/hr/master/HrOrgMasterSection';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { cn } from '@/utils/cn';

type MasterTab =
  | 'leave'
  | 'employment'
  | 'documents'
  | OrgMasterTab
  | 'overtime'
  | 'shiftTypes'
  | 'expenseTypes'
  | 'pipelineStages';

const TABS: { id: MasterTab; label: string; description: string }[] = [
  { id: 'leave', label: 'Leave types', description: 'Define leave categories and accrual policies per company' },
  { id: 'employment', label: 'Employment types', description: 'Contract types used when hiring and changing employment' },
  { id: 'documents', label: 'Document types', description: 'Categories for employee document uploads and expiry tracking' },
  ...ORG_MASTER_TABS,
  { id: 'overtime', label: 'Overtime types', description: 'Classify overtime by calendar context — each company defines its own' },
  { id: 'shiftTypes', label: 'Shift types', description: 'Categories for shift definitions (fixed, flexible, rotational, etc.)' },
  { id: 'expenseTypes', label: 'Expense types', description: 'Categories for employee expense claims and reimbursements' },
  {
    id: 'pipelineStages',
    label: 'Pipeline stages',
    description: 'Recruitment kanban columns — name, order, and color per company',
  },
];

const PAYROLL_MODES = [
  { value: 'MONTHLY_SALARY', label: 'Monthly salary' },
  { value: 'HOURLY', label: 'Hourly' },
  { value: 'CONTRACT_RULES', label: 'Contract rules' },
];

const ACCRUAL_TYPES = [
  { value: 'NONE', label: 'No accrual' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'YEARLY', label: 'Yearly (lump)' },
];

const DAY_CLASS_OPTIONS = [
  { value: 'ANY', label: 'Any day' },
  { value: 'WORKING', label: 'Scheduled working day' },
  { value: 'HOLIDAY', label: 'Holiday' },
  { value: 'WEEKEND', label: 'Weekend / off day' },
];

function boolLabel(v: boolean) {
  return v ? 'Yes' : 'No';
}

export function HrMasterDataPage({ defaultTab = 'leave' }: { defaultTab?: MasterTab }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as MasterTab) || defaultTab;

  const [leaveTypes, setLeaveTypes] = useState<HrLeaveType[]>([]);
  const [policies, setPolicies] = useState<HrLeavePolicy[]>([]);
  const [employmentTypes, setEmploymentTypes] = useState<HrEmploymentType[]>([]);
  const [documentTypes, setDocumentTypes] = useState<HrDocumentType[]>([]);
  const [overtimeTypes, setOvertimeTypes] = useState<HrOvertimeType[]>([]);
  const [shiftTypes, setShiftTypes] = useState<HrShiftType[]>([]);
  const [expenseTypes, setExpenseTypes] = useState<HrExpenseType[]>([]);
  const [pipelineStages, setPipelineStages] = useState<HrRecruitmentPipelineStage[]>([]);
  const [orgStructure, setOrgStructure] = useState<HrOrgStructure | null>(null);
  const [loading, setLoading] = useState(false);
  const [orgCreateOpen, setOrgCreateOpen] = useState(false);

  const [leaveModal, setLeaveModal] = useState<HrLeaveType | null | 'new'>(null);
  const [employmentModal, setEmploymentModal] = useState<HrEmploymentType | null | 'new'>(null);
  const [documentModal, setDocumentModal] = useState<HrDocumentType | null | 'new'>(null);
  const [overtimeModal, setOvertimeModal] = useState<HrOvertimeType | null | 'new'>(null);
  const [shiftTypeModal, setShiftTypeModal] = useState<HrShiftType | null | 'new'>(null);
  const [expenseTypeModal, setExpenseTypeModal] = useState<HrExpenseType | null | 'new'>(null);
  const [pipelineStageModal, setPipelineStageModal] = useState<HrRecruitmentPipelineStage | null | 'new'>(null);
  const [policyTypeId, setPolicyTypeId] = useState('');
  const [policyName, setPolicyName] = useState('');
  const [accrualType, setAccrualType] = useState('NONE');
  const [accrualDays, setAccrualDays] = useState('0');
  const [workforceSettings, setWorkforceSettings] = useState<HrWorkforceSettings | null>(null);
  const [savingWorkforce, setSavingWorkforce] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lt, pol, et, dt, org, ot, st, ex, ps, ws] = await Promise.all([
        hrApi.listLeaveTypes(),
        hrApi.listLeavePolicies(),
        hrApi.listEmploymentTypesAdmin(),
        hrApi.listDocumentTypes(),
        hrApi.getOrgStructure(),
        hrApi.listOvertimeTypes(),
        hrApi.listShiftTypes(),
        hrApi.listExpenseTypes(),
        hrApi.listRecruitmentPipelineStages(),
        hrApi.getWorkforceSettings(),
      ]);
      setLeaveTypes(lt);
      setPolicies(pol);
      setEmploymentTypes(et);
      setDocumentTypes(dt);
      refreshOrgStructureCache(org);
      setOrgStructure(org);
      setOvertimeTypes(ot);
      setShiftTypes(st);
      setExpenseTypes(ex);
      setPipelineStages(ps);
      setWorkforceSettings(ws);
    } catch (err) {
      showApiError(err, 'Failed to load HR master data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setTab = (next: MasterTab) => {
    setSearchParams({ tab: next });
  };

  const policyByTypeId = useMemo(() => {
    const map = new Map<number, HrLeavePolicy>();
    for (const p of policies) map.set(p.leaveTypeId, p);
    return map;
  }, [policies]);

  const leaveColumns: Column<HrLeaveType>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name' },
    { key: 'paid', header: 'Paid', render: (r) => boolLabel(r.paid) },
    { key: 'carryForward', header: 'Carry forward', render: (r) => boolLabel(r.carryForward) },
    {
      key: 'id',
      header: 'Policy',
      render: (r) => policyByTypeId.get(r.id)?.policyName ?? '—',
    },
    {
      key: 'active',
      header: 'Status',
      render: (r) => (
        <span className={cn('rounded px-2 py-0.5 text-xs font-medium', r.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600')}>
          {r.active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      actions: true,
      render: (r) => (
        <PageActionGate action="edit">
          <TableRowActions>
            <Button variant="secondary" size="sm" onClick={() => setLeaveModal(r)}>
              Edit
            </Button>
            {r.active && (
              <Button variant="secondary" size="sm" onClick={() => void deactivateLeave(r.id)}>
                Deactivate
              </Button>
            )}
          </TableRowActions>
        </PageActionGate>
      ),
    },
  ];

  const employmentColumns: Column<HrEmploymentType>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name' },
    { key: 'payrollMode', header: 'Payroll mode' },
    { key: 'defaultHoursPerDay', header: 'Hours/day' },
    { key: 'requiresEndDate', header: 'End date required', render: (r) => boolLabel(r.requiresEndDate) },
    {
      key: 'active',
      header: 'Status',
      render: (r) => (
        <span className={cn('rounded px-2 py-0.5 text-xs font-medium', r.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600')}>
          {r.active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      actions: true,
      render: (r) => (
        <PageActionGate action="edit">
          <TableRowActions>
            <Button variant="secondary" size="sm" onClick={() => setEmploymentModal(r)}>
              Edit
            </Button>
            {r.active && (
              <Button variant="secondary" size="sm" onClick={() => void deactivateEmployment(r.id)}>
                Deactivate
              </Button>
            )}
          </TableRowActions>
        </PageActionGate>
      ),
    },
  ];

  const documentColumns: Column<HrDocumentType>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name' },
    { key: 'expiryTracked', header: 'Track expiry', render: (r) => boolLabel(r.expiryTracked) },
    {
      key: 'actions',
      header: '',
      actions: true,
      render: (r) => (
        <PageActionGate action="edit">
          <TableRowActions>
            <Button variant="secondary" size="sm" onClick={() => setDocumentModal(r)}>
              Edit
            </Button>
            <Button variant="secondary" size="sm" onClick={() => void deactivateDocument(r.id)}>
              Remove
            </Button>
          </TableRowActions>
        </PageActionGate>
      ),
    },
  ];

  const overtimeColumns: Column<HrOvertimeType>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name' },
    { key: 'applicableDayClass', header: 'Day class' },
    { key: 'manualRequestAllowed', header: 'Manual request', render: (r) => boolLabel(r.manualRequestAllowed) },
    { key: 'autoAssign', header: 'Auto on attendance', render: (r) => boolLabel(r.autoAssign) },
    {
      key: 'active',
      header: 'Status',
      render: (r) => (
        <span className={cn('rounded px-2 py-0.5 text-xs font-medium', r.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600')}>
          {r.active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      actions: true,
      render: (r) => (
        <PageActionGate action="edit">
          <TableRowActions>
            <Button variant="secondary" size="sm" onClick={() => setOvertimeModal(r)}>
              Edit
            </Button>
            {r.active && (
              <Button variant="secondary" size="sm" onClick={() => void deactivateOvertime(r.id)}>
                Deactivate
              </Button>
            )}
          </TableRowActions>
        </PageActionGate>
      ),
    },
  ];

  const shiftTypeColumns: Column<HrShiftType>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name' },
    { key: 'description', header: 'Description', render: (r) => r.description ?? '—' },
    {
      key: 'active',
      header: 'Status',
      render: (r) => (
        <span className={cn('rounded px-2 py-0.5 text-xs font-medium', r.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600')}>
          {r.active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      actions: true,
      render: (r) => (
        <PageActionGate action="edit">
          <TableRowActions>
            <Button variant="secondary" size="sm" onClick={() => setShiftTypeModal(r)}>
              Edit
            </Button>
            {r.active && (
              <Button variant="secondary" size="sm" onClick={() => void deactivateShiftType(r.id)}>
                Deactivate
              </Button>
            )}
          </TableRowActions>
        </PageActionGate>
      ),
    },
  ];

  const pipelineStageColumns: Column<HrRecruitmentPipelineStage>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name' },
    {
      key: 'color',
      header: 'Color',
      render: (r) => (
        <div className="flex items-center gap-2">
          <span
            className="h-5 w-5 rounded border border-slate-300 dark:border-slate-600"
            style={{ backgroundColor: r.color }}
            aria-hidden
          />
          <span className="font-mono text-xs text-slate-600 dark:text-slate-300">{r.color}</span>
        </div>
      ),
    },
    { key: 'sortOrder', header: 'Order' },
    {
      key: 'active',
      header: 'Status',
      render: (r) => (
        <span
          className={cn(
            'rounded px-2 py-0.5 text-xs font-medium',
            r.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600',
          )}
        >
          {r.active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      actions: true,
      render: (r) => (
        <PageActionGate action="edit">
          <TableRowActions>
            <Button variant="secondary" size="sm" onClick={() => setPipelineStageModal(r)}>
              Edit
            </Button>
            {r.active && (
              <Button variant="secondary" size="sm" onClick={() => void deactivatePipelineStage(r.id)}>
                Deactivate
              </Button>
            )}
          </TableRowActions>
        </PageActionGate>
      ),
    },
  ];

  const expenseTypeColumns: Column<HrExpenseType>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name' },
    { key: 'requiresReceipt', header: 'Receipt required', render: (r) => boolLabel(r.requiresReceipt) },
    {
      key: 'active',
      header: 'Status',
      render: (r) => (
        <span className={cn('rounded px-2 py-0.5 text-xs font-medium', r.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600')}>
          {r.active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      actions: true,
      render: (r) => (
        <PageActionGate action="edit">
          <TableRowActions>
            <Button variant="secondary" size="sm" onClick={() => setExpenseTypeModal(r)}>
              Edit
            </Button>
            {r.active && (
              <Button variant="secondary" size="sm" onClick={() => void deactivateExpenseType(r.id)}>
                Deactivate
              </Button>
            )}
          </TableRowActions>
        </PageActionGate>
      ),
    },
  ];

  async function saveWorkforceSettings() {
    if (!workforceSettings) return;
    setSavingWorkforce(true);
    try {
      const saved = await hrApi.saveWorkforceSettings(workforceSettings);
      setWorkforceSettings(saved);
      notify.success('Leave configuration saved');
    } catch (err) {
      showApiError(err, 'Failed to save leave configuration');
    } finally {
      setSavingWorkforce(false);
    }
  }

  async function deactivateLeave(id: number) {
    try {
      await hrApi.deactivateLeaveType(id);
      notify.success('Leave type deactivated');
      load();
    } catch (err) {
      showApiError(err, 'Failed to deactivate leave type');
    }
  }

  async function deactivateEmployment(id: number) {
    try {
      await hrApi.deactivateEmploymentType(id);
      notify.success('Employment type deactivated');
      load();
    } catch (err) {
      showApiError(err, 'Failed to deactivate employment type');
    }
  }

  async function deactivateDocument(id: number) {
    try {
      await hrApi.deactivateDocumentType(id);
      notify.success('Document type removed');
      load();
    } catch (err) {
      showApiError(err, 'Failed to remove document type');
    }
  }

  async function deactivateOvertime(id: number) {
    try {
      await hrApi.deactivateOvertimeType(id);
      notify.success('Overtime type deactivated');
      load();
    } catch (err) {
      showApiError(err, 'Failed to deactivate overtime type');
    }
  }

  async function deactivateShiftType(id: number) {
    try {
      await hrApi.deactivateShiftType(id);
      notify.success('Shift type deactivated');
      load();
    } catch (err) {
      showApiError(err, 'Failed to deactivate shift type');
    }
  }

  async function deactivateExpenseType(id: number) {
    try {
      await hrApi.deactivateExpenseType(id);
      notify.success('Expense type deactivated');
      load();
    } catch (err) {
      showApiError(err, 'Failed to deactivate expense type');
    }
  }

  async function deactivatePipelineStage(id: number) {
    try {
      await hrApi.deactivateRecruitmentPipelineStage(id);
      notify.success('Pipeline stage deactivated');
      load();
    } catch (err) {
      showApiError(err, 'Failed to deactivate pipeline stage');
    }
  }

  async function savePolicy() {
    if (!policyTypeId || !policyName.trim()) return;
    try {
      await hrApi.saveLeavePolicy({
        leaveTypeId: Number(policyTypeId),
        policyName: policyName.trim(),
        accrualRuleJson: JSON.stringify({
          type: accrualType,
          days: Number(accrualDays) || 0,
        }),
      });
      notify.success('Leave policy saved');
      setPolicyTypeId('');
      setPolicyName('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to save policy');
    }
  }

  const activeTabMeta = TABS.find((t) => t.id === tab) ?? TABS[0];

  return (
    <div className="space-y-6">
      <PageHeader
        title="HR master data"
        description="Configure HR masters per company — leave, employment, org units, documents, overtime, shifts, and expenses."
        breadcrumbs={[{ label: 'HR', to: '/hr' }, { label: 'Master data' }]}
        actions={
          <PageActionGate action="create">
            <Button
              onClick={() => {
                if (tab === 'leave') setLeaveModal('new');
                else if (tab === 'employment') setEmploymentModal('new');
                else if (tab === 'documents') setDocumentModal('new');
                else if (isOrgMasterTab(tab)) setOrgCreateOpen(true);
                else if (tab === 'overtime') setOvertimeModal('new');
                else if (tab === 'shiftTypes') setShiftTypeModal('new');
                else if (tab === 'pipelineStages') setPipelineStageModal('new');
                else setExpenseTypeModal('new');
              }}
            >
              Add {activeTabMeta.label.toLowerCase().replace(/ types$/, ' type').replace(/s$/, '')}
            </Button>
          </PageActionGate>
        }
      />

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-1 dark:border-slate-700">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={cn(
              'rounded-t-lg px-4 py-2 text-sm font-medium transition-colors',
              tab === t.id
                ? 'border border-b-0 border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200',
            )}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'leave' && (
        <div className="space-y-6">
          <DataTable columns={leaveColumns} data={leaveTypes} loading={loading} keyField="id" emptyTitle="No leave types yet — add your own" />
          <section className="card space-y-3 p-4">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">Leave request modes</h3>
            <p className="text-sm text-muted">Configure which leave duration types employees can request.</p>
            {workforceSettings && (
              <FormGrid columns={4}>
                <Select
                  label="Full day"
                  value={workforceSettings.fullDayEnabled ? 'true' : 'false'}
                  onChange={(v) => setWorkforceSettings({ ...workforceSettings, fullDayEnabled: v === 'true' })}
                  options={[
                    { value: 'true', label: 'Enabled' },
                    { value: 'false', label: 'Disabled' },
                  ]}
                />
                <Select
                  label="Half day"
                  value={workforceSettings.halfDayEnabled ? 'true' : 'false'}
                  onChange={(v) => setWorkforceSettings({ ...workforceSettings, halfDayEnabled: v === 'true' })}
                  options={[
                    { value: 'true', label: 'Enabled' },
                    { value: 'false', label: 'Disabled' },
                  ]}
                />
                <Select
                  label="Hourly"
                  value={workforceSettings.hourlyEnabled ? 'true' : 'false'}
                  onChange={(v) => setWorkforceSettings({ ...workforceSettings, hourlyEnabled: v === 'true' })}
                  options={[
                    { value: 'true', label: 'Enabled' },
                    { value: 'false', label: 'Disabled' },
                  ]}
                />
                <Input
                  label="Max hourly leave / day"
                  type="number"
                  min={0}
                  step={0.5}
                  value={String(workforceSettings.maxHourlyLeavePerDay)}
                  onChange={(e) =>
                    setWorkforceSettings({
                      ...workforceSettings,
                      maxHourlyLeavePerDay: Number(e.target.value),
                    })
                  }
                />
              </FormGrid>
            )}
            {workforceSettings && (
              <PageActionGate action="edit">
                <FormActions
                  primaryLabel="Save leave configuration"
                  primaryLoading={savingWorkforce}
                  onPrimary={() => void saveWorkforceSettings()}
                  bordered={false}
                />
              </PageActionGate>
            )}
          </section>
          <section className="card space-y-3 p-4">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">Accrual policy</h3>
            <FormGrid columns={4}>
              <Select
                label="Leave type"
                value={policyTypeId}
                onChange={setPolicyTypeId}
                placeholder="Select type"
                options={leaveTypes.filter((t) => t.active).map((t) => ({ value: String(t.id), label: t.name }))}
              />
              <Input label="Policy name" value={policyName} onChange={(e) => setPolicyName(e.target.value)} />
              <Select label="Accrual" value={accrualType} onChange={setAccrualType} options={ACCRUAL_TYPES} />
              <Input label="Days per period" type="number" min={0} step={0.5} value={accrualDays} onChange={(e) => setAccrualDays(e.target.value)} />
            </FormGrid>
            <PageActionGate action="edit">
              <FormActions
                primaryLabel="Save policy"
                onPrimary={() => void savePolicy()}
                onReset={() => {
                  setPolicyTypeId('');
                  setPolicyName('');
                  setAccrualType('NONE');
                  setAccrualDays('1');
                }}
                bordered={false}
              />
            </PageActionGate>
          </section>
        </div>
      )}

      {tab === 'employment' && (
        <DataTable
          columns={employmentColumns}
          data={employmentTypes}
          loading={loading}
          keyField="id"
          emptyTitle="No employment types — add Full-time, Contract, Intern, etc."
        />
      )}

      {tab === 'documents' && (
        <DataTable
          columns={documentColumns}
          data={documentTypes}
          loading={loading}
          keyField="id"
          emptyTitle="No document types — add Passport, Visa, Contract, etc."
        />
      )}

      {isOrgMasterTab(tab) && (
        <HrOrgMasterSection
          tab={tab}
          structure={orgStructure}
          loading={loading}
          createOpen={orgCreateOpen}
          onCreateClose={() => setOrgCreateOpen(false)}
          onRefresh={load}
        />
      )}

      {tab === 'overtime' && (
        <DataTable
          columns={overtimeColumns}
          data={overtimeTypes}
          loading={loading}
          keyField="id"
          emptyTitle="No overtime types yet — add types for working day, holiday, weekend, etc."
        />
      )}

      {tab === 'shiftTypes' && (
        <DataTable
          columns={shiftTypeColumns}
          data={shiftTypes}
          loading={loading}
          keyField="id"
          emptyTitle="No shift types yet — add Fixed, Flexible, Rotational, or your own categories"
        />
      )}

      {tab === 'pipelineStages' && (
        <DataTable
          columns={pipelineStageColumns}
          data={pipelineStages}
          loading={loading}
          keyField="id"
          emptyTitle="No pipeline stages — defaults are created on first load"
        />
      )}

      {tab === 'expenseTypes' && (
        <DataTable
          columns={expenseTypeColumns}
          data={expenseTypes}
          loading={loading}
          keyField="id"
          emptyTitle="No expense types yet — add Travel, Meals, Supplies, etc."
        />
      )}

      <LeaveTypeModal open={leaveModal != null} editing={leaveModal === 'new' ? null : leaveModal} onClose={() => setLeaveModal(null)} onSaved={load} />
      <EmploymentTypeModal
        open={employmentModal != null}
        editing={employmentModal === 'new' ? null : employmentModal}
        onClose={() => setEmploymentModal(null)}
        onSaved={load}
      />
      <DocumentTypeModal
        open={documentModal != null}
        editing={documentModal === 'new' ? null : documentModal}
        onClose={() => setDocumentModal(null)}
        onSaved={load}
      />
      <OvertimeTypeModal
        open={overtimeModal != null}
        editing={overtimeModal === 'new' ? null : overtimeModal}
        onClose={() => setOvertimeModal(null)}
        onSaved={load}
      />
      <ShiftTypeModal
        open={shiftTypeModal != null}
        editing={shiftTypeModal === 'new' ? null : shiftTypeModal}
        onClose={() => setShiftTypeModal(null)}
        onSaved={load}
      />
      <ExpenseTypeModal
        open={expenseTypeModal != null}
        editing={expenseTypeModal === 'new' ? null : expenseTypeModal}
        onClose={() => setExpenseTypeModal(null)}
        onSaved={load}
      />
      <PipelineStageModal
        open={pipelineStageModal != null}
        editing={pipelineStageModal === 'new' ? null : pipelineStageModal}
        onClose={() => setPipelineStageModal(null)}
        onSaved={load}
      />
    </div>
  );
}

function LeaveTypeModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: HrLeaveType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [paid, setPaid] = useState('true');
  const [carryForward, setCarryForward] = useState('false');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? '');
    setCode(editing?.code ?? '');
    setPaid(editing?.paid === false ? 'false' : 'true');
    setCarryForward(editing?.carryForward ? 'true' : 'false');
  }, [open, editing]);

  const save = async () => {
    if (!name.trim() || !code.trim()) return;
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        paid: paid === 'true',
        carryForward: carryForward === 'true',
        active: true,
      };
      if (editing) await hrApi.updateLeaveType(editing.id, body);
      else await hrApi.createLeaveType(body);
      notify.success(editing ? 'Leave type updated' : 'Leave type created');
      onSaved();
      onClose();
    } catch (err) {
      showApiError(err, 'Failed to save leave type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? 'Edit leave type' : 'New leave type'}>
      <FormGrid columns={2} className="gap-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. ANNUAL" />
        <Select label="Paid leave" value={paid} onChange={setPaid} options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]} />
        <Select
          label="Carry forward"
          value={carryForward}
          onChange={setCarryForward}
          options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]}
        />
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Save
          </Button>
        </div>
      </FormGrid>
    </Modal>
  );
}

function EmploymentTypeModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: HrEmploymentType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [payrollMode, setPayrollMode] = useState('MONTHLY_SALARY');
  const [hours, setHours] = useState('8');
  const [requiresEndDate, setRequiresEndDate] = useState('false');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? '');
    setCode(editing?.code ?? '');
    setPayrollMode(editing?.payrollMode ?? 'MONTHLY_SALARY');
    setHours(editing?.defaultHoursPerDay != null ? String(editing.defaultHoursPerDay) : '');
    setRequiresEndDate(editing?.requiresEndDate ? 'true' : 'false');
  }, [open, editing]);

  const save = async () => {
    if (!name.trim() || !code.trim()) return;
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        payrollMode,
        requiresEndDate: requiresEndDate === 'true',
        defaultHoursPerDay: hours ? Number(hours) : undefined,
        active: true,
      };
      if (editing) await hrApi.updateEmploymentType(editing.id, body);
      else await hrApi.createEmploymentType(body);
      notify.success(editing ? 'Employment type updated' : 'Employment type created');
      onSaved();
      onClose();
    } catch (err) {
      showApiError(err, 'Failed to save employment type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? 'Edit employment type' : 'New employment type'}>
      <FormGrid columns={2} className="gap-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. FULL_TIME" />
        <Select label="Payroll mode" value={payrollMode} onChange={setPayrollMode} options={PAYROLL_MODES} />
        <Input label="Default hours/day" type="number" min={0} step={0.5} value={hours} onChange={(e) => setHours(e.target.value)} />
        <Select
          label="Requires end date"
          value={requiresEndDate}
          onChange={setRequiresEndDate}
          options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]}
        />
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Save
          </Button>
        </div>
      </FormGrid>
    </Modal>
  );
}

function DocumentTypeModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: HrDocumentType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [expiryTracked, setExpiryTracked] = useState('false');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? '');
    setCode(editing?.code ?? '');
    setExpiryTracked(editing?.expiryTracked ? 'true' : 'false');
  }, [open, editing]);

  const save = async () => {
    if (!name.trim() || !code.trim()) return;
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        expiryTracked: expiryTracked === 'true',
      };
      if (editing) await hrApi.updateDocumentType(editing.id, body);
      else await hrApi.createDocumentType(body);
      notify.success(editing ? 'Document type updated' : 'Document type created');
      onSaved();
      onClose();
    } catch (err) {
      showApiError(err, 'Failed to save document type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? 'Edit document type' : 'New document type'}>
      <FormGrid columns={2} className="gap-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. PASSPORT" />
        <Select
          label="Track expiry date"
          value={expiryTracked}
          onChange={setExpiryTracked}
          options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]}
        />
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Save
          </Button>
        </div>
      </FormGrid>
    </Modal>
  );
}

function OvertimeTypeModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: HrOvertimeType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [dayClass, setDayClass] = useState('ANY');
  const [manualRequest, setManualRequest] = useState('true');
  const [autoAssign, setAutoAssign] = useState('false');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? '');
    setCode(editing?.code ?? '');
    setDescription(editing?.description ?? '');
    setDayClass(editing?.applicableDayClass ?? 'ANY');
    setManualRequest(editing?.manualRequestAllowed === false ? 'false' : 'true');
    setAutoAssign(editing?.autoAssign ? 'true' : 'false');
  }, [open, editing]);

  const save = async () => {
    if (!name.trim() || !code.trim()) return;
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        description: description.trim() || undefined,
        applicableDayClass: dayClass,
        manualRequestAllowed: manualRequest === 'true',
        autoAssign: autoAssign === 'true',
        active: true,
      };
      if (editing) await hrApi.updateOvertimeType(editing.id, body);
      else await hrApi.createOvertimeType(body);
      notify.success(editing ? 'Overtime type updated' : 'Overtime type created');
      onSaved();
      onClose();
    } catch (err) {
      showApiError(err, 'Failed to save overtime type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? 'Edit overtime type' : 'New overtime type'}>
      <FormGrid columns={2} className="gap-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. WEEKEND_OT" />
        <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} className="col-span-2" />
        <Select label="Applicable day" value={dayClass} onChange={setDayClass} options={DAY_CLASS_OPTIONS} />
        <Select
          label="Allow manual requests"
          value={manualRequest}
          onChange={setManualRequest}
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No (attendance auto-assign only)' },
          ]}
        />
        <Select
          label="Auto-assign from attendance"
          value={autoAssign}
          onChange={setAutoAssign}
          options={[
            { value: 'false', label: 'No' },
            { value: 'true', label: 'Yes' },
          ]}
        />
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Save
          </Button>
        </div>
      </FormGrid>
    </Modal>
  );
}

function ShiftTypeModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: HrShiftType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? '');
    setCode(editing?.code ?? '');
    setDescription(editing?.description ?? '');
  }, [open, editing]);

  const save = async () => {
    if (!name.trim() || !code.trim()) return;
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        description: description.trim() || undefined,
        active: true,
      };
      if (editing) await hrApi.updateShiftType(editing.id, body);
      else await hrApi.createShiftType(body);
      notify.success(editing ? 'Shift type updated' : 'Shift type created');
      onSaved();
      onClose();
    } catch (err) {
      showApiError(err, 'Failed to save shift type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? 'Edit shift type' : 'New shift type'}>
      <FormGrid columns={2} className="gap-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. FIXED" />
        <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} className="col-span-2" />
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Save
          </Button>
        </div>
      </FormGrid>
    </Modal>
  );
}

function ExpenseTypeModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: HrExpenseType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [requiresReceipt, setRequiresReceipt] = useState('false');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? '');
    setCode(editing?.code ?? '');
    setDescription(editing?.description ?? '');
    setRequiresReceipt(editing?.requiresReceipt ? 'true' : 'false');
  }, [open, editing]);

  const save = async () => {
    if (!name.trim() || !code.trim()) return;
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        description: description.trim() || undefined,
        requiresReceipt: requiresReceipt === 'true',
        active: true,
      };
      if (editing) await hrApi.updateExpenseType(editing.id, body);
      else await hrApi.createExpenseType(body);
      notify.success(editing ? 'Expense type updated' : 'Expense type created');
      onSaved();
      onClose();
    } catch (err) {
      showApiError(err, 'Failed to save expense type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? 'Edit expense type' : 'New expense type'}>
      <FormGrid columns={2} className="gap-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. TRAVEL" />
        <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} className="col-span-2" />
        <Select
          label="Receipt required"
          value={requiresReceipt}
          onChange={setRequiresReceipt}
          options={[
            { value: 'false', label: 'No' },
            { value: 'true', label: 'Yes' },
          ]}
        />
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Save
          </Button>
        </div>
      </FormGrid>
    </Modal>
  );
}

function PipelineStageModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: HrRecruitmentPipelineStage | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [color, setColor] = useState('#64748b');
  const [sortOrder, setSortOrder] = useState('100');
  const [saving, setSaving] = useState(false);
  const codeLocked = Boolean(editing?.systemDefault);

  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? '');
    setCode(editing?.code ?? '');
    setColor(editing?.color ?? '#64748b');
    setSortOrder(String(editing?.sortOrder ?? 100));
  }, [open, editing]);

  const save = async () => {
    if (!name.trim() || !code.trim()) return;
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        color: color.trim(),
        sortOrder: Number(sortOrder) || 0,
        active: true,
      };
      if (editing) await hrApi.updateRecruitmentPipelineStage(editing.id, body);
      else await hrApi.createRecruitmentPipelineStage(body);
      notify.success(editing ? 'Pipeline stage updated' : 'Pipeline stage created');
      onSaved();
      onClose();
    } catch (err) {
      showApiError(err, 'Failed to save pipeline stage');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? 'Edit pipeline stage' : 'New pipeline stage'}>
      <FormGrid columns={2} className="gap-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Phone screen" />
        <Input
          label="Code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="e.g. PHONE_SCREEN"
          disabled={codeLocked}
        />
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={color.startsWith('#') ? color : `#${color}`}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-14 cursor-pointer rounded border border-slate-300 bg-white p-1 dark:border-slate-600"
              aria-label="Stage color"
            />
            <Input value={color} onChange={(e) => setColor(e.target.value)} placeholder="#0ea5e9" />
          </div>
        </div>
        <Input
          label="Sort order"
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          placeholder="10"
        />
        {codeLocked && (
          <p className="col-span-2 text-xs text-muted">
            Built-in stage codes cannot be changed. You can rename, reorder, and recolor them.
          </p>
        )}
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => void save()}>
            Save
          </Button>
        </div>
      </FormGrid>
    </Modal>
  );
}
